import { useState, useMemo, ReactNode } from "react"
import classNames from "classnames"
import { NoResultsMessage } from "./NoResultsMessage"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import SearchInput from "@/components/shared/SearchInput"
import Spinner, { SpinnerSize } from "@/components/shared/Spinner"
import { ColorVariant } from "@/types/index"
import "./index.scss"

interface SearchAndSelectProps<T> {
   items?: T[]
   title: ReactNode
   isLoading?: boolean
   noResultsTitleTerm: string
   withBackButton?: boolean
   removePadding?: boolean
   onCancel: () => void
   onSave: (id: string) => void
   filter: (item: T, searchTerm: string) => boolean
   renderItem: (
      item: T,
      isSelected: boolean,
      handleSelect: (item: T) => void
   ) => ReactNode
}

export function SearchAndSelect<T extends { id: string }>({
   items = [],
   title,
   isLoading = false,
   noResultsTitleTerm,
   withBackButton = true,
   removePadding = false,
   onCancel,
   onSave,
   filter,
   renderItem,
}: SearchAndSelectProps<T>) {
   const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
   const [searchTerm, setSearchTerm] = useState("")

   const filteredItems = useMemo(() => {
      if (!searchTerm) return items
      return items.filter((item) => filter(item, searchTerm))
   }, [searchTerm, items, filter])

   const handleSearch = (value: string) => {
      setSearchTerm(value)
   }

   const handleSelectItem = (item: T) => {
      if (selectedItemId === item.id) {
         return
      }
      setSelectedItemId(item.id)
   }

   const handleSave = () => {
      if (selectedItemId) {
         onSave(selectedItemId)
      }
   }

   return (
      <div
         className={classNames("search-and-select", {
            "search-and-select--no-padding": removePadding,
         })}
      >
         <div className="search-and-select__header">
            <div className="search-and-select__title">{title}</div>
         </div>

         {items.length > 0 && (
            <SearchInput
               onSearch={handleSearch}
               placeholder="Search"
            />
         )}

         <div className="search-and-select__list">
            {isLoading ? (
               <div className="search-and-select--loading">
                  <Spinner size={SpinnerSize.SMALL} />
               </div>
            ) : items.length === 0 ? (
               <NoResultsMessage titleTerm={noResultsTitleTerm} />
            ) : filteredItems.length > 0 ? (
               filteredItems.map((item) =>
                  renderItem(item, selectedItemId === item.id, handleSelectItem)
               )
            ) : (
               searchTerm && (
                  <NoResultsMessage
                     titleTerm={noResultsTitleTerm}
                     searchTerm={searchTerm}
                  />
               )
            )}
         </div>

         <div className="search-and-select__actions">
            {withBackButton ? (
               <Button
                  color={ColorVariant.HONOLULU_BLUE}
                  iconPosition={ButtonIconPosition.LEFT}
                  onClick={onCancel}
                  iconName="arrowLeft"
               >
                  Back
               </Button>
            ) : (
               <Button
                  color={ColorVariant.HONOLULU_BLUE}
                  onClick={onCancel}
               >
                  Cancel
               </Button>
            )}
            <Button
               color={ColorVariant.HONOLULU_BLUE}
               iconPosition={ButtonIconPosition.LEFT}
               onClick={handleSave}
               iconName="save"
               disabled={!selectedItemId}
            >
               Save
            </Button>
         </div>
      </div>
   )
}

export default SearchAndSelect
